import { useFormik, FormikErrors } from 'formik';
import { useCallback } from 'react';
import moment from 'moment';
import { useFieldPropsGetter } from '../form/useFieldPropsGetter';
import { useAxios } from '../hooks/useAxios';
import { useNotification } from '../notifications';
import { useTranslation } from 'react-i18next';

export type FormData = {
  date: number;
  time: string;
};

export function useRemindMeForm(aiId: string) {
  const { t } = useTranslation();
  const now = new Date();
  const notify = useNotification();

  const [, postReminder] = useAxios(
    {
      url: 'activity/AddRemindMeActivity',
      method: 'POST',
    },
    {
      manual: true,
    }
  );

  const setReminder = useCallback(
    async (formData: FormData) => {
      const dt = moment(formData.date);
      const [hours, minutes] = formData.time
        .split(':')
        .map((n) => parseInt(n, 10));
      dt.hours(hours);
      dt.minutes(minutes);

      try {
        await postReminder({
          data: {
            aid: aiId,
            dateAndTime: dt.format('YYYY-MM-DD HH:mm'),
          },
        });
        notify.success(t('Reminder added'));
      } catch (e) {
        notify.error(
          t('There was a problem while adding a reminder. Please try again later.')
        );
        throw e;
      }
    },
    [aiId, postReminder, notify]
  );

  const {
    submitForm,
    setFieldValue,
    errors: formErrors,
    isSubmitting,
    isValidating,
    values,
  } = useFormik<FormData>({
    initialValues: {
      date: now.getTime(),
      time: moment(now).format('HH:mm'),
    },
    validate: (data) => {
      const errors: FormikErrors<FormData> = {};
      if (!data.date) {
        errors.date = t('Field is required');
      }
      if (!data.time) {
        errors.time = t('Field is required');
      }
      return errors;
    },
    onSubmit: setReminder,
  });
  const isProcessing = isSubmitting || isValidating;

  const getFieldProps = useFieldPropsGetter<FormData>({
    formErrors,
    isProcessing,
    setFieldValue,
    values,
  });

  return {
    values,
    getFieldProps,
    submitForm,
    isProcessing,
    formErrors,
  };
}
